import { useEffect, useRef } from 'react'
import { BrowserRouter } from 'react-router-dom'
import { AppRoutes } from './routes'
import { ToastProvider } from './components/radix'
import { LocationChangeProvider } from './auth/context/LocationChangeContext'
import { useMe } from './auth/hooks/useMe'
import { useBuildVersionCheck } from './hooks/useBuildVersionCheck'
import { identifyUser, resetPostHog } from './lib/posthog'
import { trackUserLoggedIn } from './lib/analytics'
import { clearCheckoutIdsOnUserChange, shouldReportLogin } from './lib/checkout-session'
import { ConditionalFeaturebaseProvider } from './lib/featurebase'
import ErrorBoundary from './components/error/ErrorBoundary'

/**
 * Identifies the authenticated user in PostHog once /api/users/me resolves,
 * and resets PostHog when a different user signs in on a shared device.
 * Renders nothing — side-effects only.
 */
function PostHogIdentifier() {
  const { data: me } = useMe()
  // The trait values we last sent to PostHog. `me` gets a new object reference
  // on every /api/users/me refetch (navigation, tab refocus, the postcode
  // flow), re-running this effect; without a guard each run re-sent identify +
  // group, spamming `Set person properties` / `Group identify`. Keyed on the
  // sent TRAITS (not just the id) so a refetch that changes nothing we send is
  // skipped, while a real change — a profile edit to name/role/tenant, which
  // invalidates ['auth-me'] — re-identifies and keeps the person/group fresh.
  const identifiedTraitsKeyRef = useRef<string | null>(null)

  useEffect(() => {
    if (!me) {
      // No reset here. `me` is briefly falsy on every page load before
      // /api/users/me resolves; resetting on that flicker (the old cleanup did,
      // via a stale-closure `!me`) minted a fresh PostHog session on every
      // refresh and split one login across many session ids — badly, on mobile,
      // where tabs reload constantly. A genuine sign-out resets in LogoutPage,
      // which runs before the browser is redirected away.
      return
    }

    // Baskets and PostHog identity are per-user. clearCheckoutIdsOnUserChange
    // tracks the last user in localStorage — so it survives the full-page
    // redirect a logout performs, unlike an in-memory ref — and returns true
    // when a DIFFERENT real user was here before (an A → B switch on a shared
    // device). Reset PostHog on that same signal so B's events are not merged
    // into A's person. LogoutPage also resets explicitly; this is the backstop
    // for a switch that skipped it (browser closed rather than signed out).
    const switchedUser = clearCheckoutIdsOnUserChange(me.id)
    if (switchedUser) {
      resetPostHog()
      // Force a fresh identify for the new user after the reset cleared it.
      identifiedTraitsKeyRef.current = null
    }

    // Re-identify only when a value we send actually changes. The key covers
    // exactly the fields passed below, so an identical refetch is skipped while
    // a profile/role/tenant edit re-syncs the person and tenant group.
    const traitsKey = JSON.stringify([
      me.id,
      me.email,
      me.first_name,
      me.last_name,
      me.role,
      me.tenant?.id ?? null,
      me.tenant?.slug ?? null,
      me.tenant?.name ?? null,
      me.is_platform_admin,
    ])
    if (identifiedTraitsKeyRef.current !== traitsKey) {
      // Cache the key only after a successful identify — if it threw internally
      // (and was swallowed), leave the ref so the next refetch retries rather
      // than skipping identification with the traits never actually sent.
      const identified = identifyUser(me.id, {
        email: me.email,
        firstName: me.first_name,
        lastName: me.last_name,
        role: me.role,
        tenantId: me.tenant?.id ?? null,
        tenantSlug: me.tenant?.slug ?? null,
        tenantName: me.tenant?.name ?? null,
        isPlatformAdmin: me.is_platform_admin,
      })
      if (identified) {
        identifiedTraitsKeyRef.current = traitsKey
      }
    }

    // Reported AFTER identifyUser, which sets the person and the tenant group
    // — firing first would land the event without a tenant. Guarded so the
    // /api/users/me resolve on every hard refresh does not report a second
    // login. is_first_login is backend-derived; the client cannot know it.
    if (shouldReportLogin(me.id)) {
      trackUserLoggedIn({
        role: me.role,
        isFirstLogin: me.is_first_login ?? false,
      })
    }
  }, [me])

  return null
}

/**
 * Keeps this tab on the live build: reloads when a newer one is deployed.
 * Renders nothing — side-effects only.
 */
function BuildVersionCheck() {
  useBuildVersionCheck()
  return null
}

function App() {
  return (
    <BrowserRouter>
      {/* Outside the ErrorBoundary so a crashed page (an old build failing
          against a newer API, say) still reloads onto the live build. */}
      <BuildVersionCheck />
      <ErrorBoundary>
        <ToastProvider>
          <PostHogIdentifier />
          <LocationChangeProvider>
            <ConditionalFeaturebaseProvider>
              <AppRoutes />
            </ConditionalFeaturebaseProvider>
          </LocationChangeProvider>
        </ToastProvider>
      </ErrorBoundary>
    </BrowserRouter>
  )
}

export default App
